<template>
  <div
    v-if="isLoading"
    class="p-20 flex justify-center"
  >
    <BaseSpinner />
  </div>
  <section
    v-else-if="useRequirementLevels && requirementLevels.length > 0"
    class="color-light-black"
  >
    <template>
      <ExpandableController
        :expanded="expanded"
        @toggle="expanded = $event"
      >
        <template #default="{ expandables, trackToggle }">
          <Expandable
            v-for="(requirementLevel, requirementLevelIndex) in requirementLevels"
            :key="`level-${requirementLevelIndex}`"
            :expandable-id="`level-${requirementLevelIndex}`"
            :expanded="`level-${requirementLevelIndex}` in expandables ? expandables[`level-${requirementLevelIndex}`] : expanded"
            class="mb-4 rounded-md border shadow-lg"
            @toggle="trackToggle({ expandableId: `level-${requirementLevelIndex}`, expandableStatus: $event })"
          >
            <template #expandable-title>
              <div class="flex-1 font-semibold text-lg md:text-xl leading-5 md:leading-6 text-left">
                <BaseIcon
                  icon="graduation-cap"
                  class="mr-4 text-gray-500"
                />
                {{ requirementLevel.label }}
              </div>
            </template>
            <template #expandable-content="{ expanded: expandedLevel }">
              <RichContent
                v-if="requirementLevel.config && requirementLevel.config.editorEnabled === true"
                class="reset-liststyle py-2 px-7 text-base md:text-lg"
                :content="getRequirementLevelNotes(requirementLevel.key)"
              />
              <div
                v-else
                class="mt-2"
              />
              <ExpandableController :expanded="expandedLevel">
                <template #expand-button><span /></template>
                <template #default="{ expandables:expandablesRequisite, trackToggle:trackToggleRequisite }">
                  <div
                    v-for="(requisite, index) in getRequirementLevels(requirementLevel.key)"
                    :key="index"
                  >
                    <template v-if="isNotNarrativeText(requisite.type)">
                      <Expandable
                        class="mb-4 rounded-md border mx-4"
                        :expandable-id="`req-${index}`"
                        :expanded="`req-${index}` in expandablesRequisite ? expandablesRequisite[`req-${index}`] : expandedLevel"
                        @toggle="trackToggleRequisite({ expandableId: `req-${index}`, expandableStatus: $event })"
                      >
                        <template #expandable-title>
                          <div class="pr-2 items-center justify-between w-full focus:outline-none color-deep-black font-semibold text-md md:text-lg leading-5 md:leading-6 text-left">
                            {{ requisite.name || 'Untitled Requisite' }}
                            {{ getRequisitesCreditsRangeLabel(relatedRequisites, requisite.rules) }}
                          </div>
                        </template>
                        <template #expandable-content="{ expanded: expandedRule }">
                          <div class="p-2 md:p-4 border-t">
                            <div class="text-base md:text-lg">
                              {{ decamelize(requisite.type) }}
                            </div>
                            <ExpandableController :expanded="expandedRule">
                              <template #expand-button><span /></template>
                              <template #default="{ expandables:expandablesRule, trackToggle:trackToggleRule }">
                                <div
                                  v-for="(rule, ruleIndex) in requisite.rules || []"
                                  :key="`req-${index}-rule-${ruleIndex}`"
                                  :class="{'border-t': ruleIndex !== 0, 'pt-2': ruleIndex === 0 }"
                                >
                                  <RequisiteSimpleRule
                                    :rule="rule"
                                    :related-requisites="relatedRequisites"
                                    :expandable-id="`req-${index}-rule-${ruleIndex}`"
                                    :expanded="`rule-${ruleIndex}` in expandablesRule ? expandablesRule[`rule-${ruleIndex}`] : expandedRule"
                                    @toggle="trackToggleRule({ expandableId: `req-${index}-rule-${ruleIndex}`, expandableStatus: $event })"
                                    @showModal="$emit('showModal', $event)"
                                  />
                                </div>
                              </template>
                            </ExpandableController>
                            <RichContent
                              v-if="isNoteNotEmpty(requisite)"
                              class="reset-liststyle pt-4 border-t text-base md:text-lg"
                              :content="requisite.notes"
                            />
                          </div>
                        </template>
                      </Expandable>
                    </template>
                    <div v-else>
                      <RichContent
                        class="py-2"
                        :content="requisite.notes"
                      />
                    </div>
                  </div>
                </template>
              </ExpandableController>
              <div
                v-if="showRequisitesCredits"
                class="text-gray-400 pb-4 px-4"
              >
                <BaseIcon
                  icon="info-circle"
                  class="small-info-icon"
                />
                <span>{{ totalRequisitesCredits(getRequirementLevels(requirementLevel.key)) }}</span>
              </div>
            </template>
          </Expandable>
          <Expandable
            v-if="requirementsWithoutLevels.length > 0"
            class="mb-4 rounded-md border shadow-lg"
            expandable-id="no-levels"
            :expanded="'no-levels' in expandables ? expandables['no-levels'] : expanded"
            @toggle="trackToggle({ expandableId: 'no-levels', expandableStatus: $event })"
          >
            <template #expandable-title>
              <BaseIcon
                icon="graduation-cap"
                class="mr-4 text-gray-500"
              />
              <div class="text-lg accordion-button flex items-center justify-between w-full focus:outline-none color-deep-black font-semibold text-lg md:text-xl leading-5 md:leading-6 text-left">
                (No Requirement Levels)
              </div>
            </template>
            <template #expandable-content="{ expanded: expandedLevel }">
              <div class="pt-2" />
              <ExpandableController :expanded="expandedLevel">
                <template #expand-button><span /></template>
                <template #default="{ expandables:expandablesRequisite, trackToggle:trackToggleRequisite }">
                  <div
                    v-for="(requisite, index) in requirementsWithoutLevels"
                    :key="`no-level-${index}`"
                  >
                    <template v-if="isNotNarrativeText(requisite.type)">
                      <Expandable
                        class="mb-4 rounded-md border mx-4"
                        :expandable-id="`req-${index}`"
                        :expanded="`req-${index}` in expandablesRequisite ? expandablesRequisite[`req-${index}`] : expandedLevel"
                        @toggle="trackToggleRequisite({ expandableId: `req-${index}`, expandableStatus: $event })"
                      >
                        <template #expandable-title>
                          <div class="pr-2 accordion-button flex items-center justify-between w-full focus:outline-none color-deep-black font-semibold text-md md:text-lg leading-5 md:leading-6 text-left">
                            {{ requisite.name || 'Untitled Requisite' }}
                            {{ getRequisitesCreditsRangeLabel(relatedRequisites, requisite.rules) }}
                          </div>
                        </template>
                        <template #expandable-content="{ expanded: expandedRule }">
                          <div class="p-2 md:p-4 border-t">
                            <div class="text-base md:text-lg">
                              {{ decamelize(requisite.type) }}
                            </div>
                            <ExpandableController :expanded="expandedRule">
                              <template #expand-button><span /></template>
                              <template #default="{ expandables:expandablesRule, trackToggle:trackToggleRule }">
                                <div
                                  v-for="(rule, ruleIndex) in requisite.rules || []"
                                  :key="`no-level-rule-${ruleIndex}`"
                                  :class="{'border-t': ruleIndex !== 0}"
                                >
                                  <RequisiteSimpleRule
                                    :rule="rule"
                                    :related-requisites="relatedRequisites"
                                    :expandable-id="`rule-${ruleIndex}`"
                                    :expanded="`rule-${ruleIndex}` in expandablesRule ? expandablesRule[`rule-${ruleIndex}`] : expandedRule"
                                    @toggle="trackToggleRule({ expandableId: `rule-${ruleIndex}`, expandableStatus: $event })"
                                    @showModal="$emit('showModal', $event)"
                                  />
                                </div>
                              </template>
                            </ExpandableController>
                            <RichContent
                              v-if="isNoteNotEmpty(requisite)"
                              class="reset-liststyle pt-4 border-t text-base md:text-lg"
                              :content="requisite.notes"
                            />
                          </div>
                        </template>
                      </Expandable>
                    </template>
                    <div v-else>
                      <RichContent
                        class="py-2 text-base md:text-lg"
                        :content="requisite.notes"
                      />
                    </div>
                  </div>
                </template>
              </ExpandableController>
              <div
                v-if="showRequisitesCredits"
                class="text-gray-400 pb-4 px-4"
              >
                <BaseIcon
                  icon="info-circle"
                  class="small-info-icon"
                />
                <span>{{ totalRequisitesCredits(requirementsWithoutLevels) }}</span>
              </div>
            </template>
          </Expandable>
        </template>
      </ExpandableController>
    </template>
  </section>
  <section v-else>
    <div>
      <ExpandableController
        :expanded="expanded"
        @toggle="expanded = $event"
      >
        <template #default="{ expandables, trackToggle }">
          <div
            v-for="(requisite, index) in sortedValue"
            :key="index"
          >
            <template v-if="isNotNarrativeText(requisite.type)">
              <Expandable
                class="mb-4 rounded-md border shadow-lg"
                :expandable-id="index"
                :expanded="index in expandables ? expandables[index] : expanded"
                @toggle="trackToggle({ expandableId: index, expandableStatus: $event })"
              >
                <template #expandable-title>
                  <BaseIcon
                    icon="graduation-cap"
                    class="mr-4 text-gray-500"
                  />
                  <div class="pr-2 accordion-button flex items-center justify-between w-full focus:outline-none color-deep-black font-semibold text-lg md:text-xl leading-5 md:leading-6 text-left">
                    {{ requisite.name || 'Untitled Requisite' }}
                    {{ getRequisitesCreditsRangeLabel(relatedRequisites, requisite.rules) }}
                  </div>
                </template>
                <template #expandable-content="{ expanded: expandedRule }">
                  <div class="p-2 md:p-4 border-t">
                    <div class="text-base md:text-lg">
                      {{ decamelize(requisite.type) }}
                    </div>
                    <ExpandableController :expanded="expandedRule">
                      <template #expand-button><span /></template>
                      <template #default="{ expandables:expandablesRule, trackToggle:trackToggleRule }">
                        <div
                          v-for="(rule, ruleIndex) in requisite.rules || []"
                          :key="`rule-${ruleIndex}`"
                          :class="{'border-t': ruleIndex !== 0 }"
                        >
                          <RequisiteSimpleRule
                            :rule="rule"
                            :related-requisites="relatedRequisites"
                            :expandable-id="`rule-${ruleIndex}`"
                            :expanded="`rule-${ruleIndex}` in expandablesRule ? expandablesRule[`rule-${ruleIndex}`] : expandedRule"
                            @toggle="trackToggleRule({ expandableId: `rule-${ruleIndex}`, expandableStatus: $event })"
                            @showModal="$emit('showModal', $event)"
                          />
                        </div>
                      </template>
                    </ExpandableController>
                    <RichContent
                      v-if="isNoteNotEmpty(requisite)"
                      class="reset-liststyle pt-4 border-t text-base md:text-lg"
                      :content="requisite.notes"
                    />
                  </div>
                </template>
              </Expandable>
            </template>
            <template v-else>
              <RichContent
                class="py-2"
                :content="requisite.notes"
              />
            </template>
          </div>
        </template>
      </ExpandableController>
      <div
        v-if="showRequisitesCredits"
        class="text-gray-400 pb-4 px-4"
      >
        <BaseIcon
          icon="info-circle"
          class="small-info-icon"
        />
        <span>{{ totalRequisitesCredits(sortedValue) }}</span>
      </div>
    </div>
  </section>
</template>

<script>
import { mapState } from 'vuex'
import _get from 'lodash/get'
import _sortBy from 'lodash/sortBy'
import { useContext } from '@nuxtjs/composition-api'
import useRequisites from '../useRequisites'
import RequisiteSimpleRule from './RequisiteSimpleRule'
import { decamelize } from '@/utils/generalUtils'
import RichContent from '@/components/RichContent'
import useFeatureFlags from '@/composables/useFeatureFlags'
import {
  CA_REQUIREMENT_LEVELS,
  SHOW_REQUISITES_SUMMED_CREDITS,
  EXPAND_ALL_DM_REQUISITE,
} from '@/constants/featureFlags'
import Expandable from '@/components/Expandable'
import ExpandableController from '@/components/ExpandableController'

export default {
  components: {
    RequisiteSimpleRule,
    RichContent,
    Expandable,
    ExpandableController,
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    documentValue: {
      type: Object,
      default: () => ({}),
    },
    entityType: {
      type: String,
      default: '',
    },
  },
  setup(props, { root }) {
    const { useRequirementLevels, showRequisitesCredits, expandAllDMRequisite } = useFeatureFlags({
      useRequirementLevels: CA_REQUIREMENT_LEVELS,
      showRequisitesCredits: SHOW_REQUISITES_SUMMED_CREDITS,
      expandAllDMRequisite: EXPAND_ALL_DM_REQUISITE,
    })
    const { $localize } = useContext()
    const {
      getRequisitesCreditsRange,
      getRequisitesCreditsRangeLabel,
      fetchRequisiteData,
    } = useRequisites({
      $axios: root.$axios,
      $store: root.$store,
      $localize,
    })

    return {
      useRequirementLevels,
      getRequisitesCreditsRange,
      getRequisitesCreditsRangeLabel,
      fetchRequisiteData,
      showRequisitesCredits,
      expanded: expandAllDMRequisite,
    }
  },
  data() {
    return {
      relatedRequisites: {
        courses: {},
        programs: {},
        courseSets: {},
      },
      isLoading: false,
      requirementLevels: [],
    }
  },
  computed: {
    ...mapState('settings', ['school']),
    requirementsWithoutLevels() {
      return this.sortedValue.filter(
        (v) =>
          this.requirementLevels
            .map((l) => l.key)
            .indexOf(v.requirementLevel) === -1
      )
    },
    documentRequirementLevels() {
      return this.documentValue.requirementLevels || []
    },
    sortedValue() {
      return _sortBy(this.value || [], 'position')
    },
    isProgram() {
      return this.entityType === 'program'
    },
  },
  async created() {
    this.isLoading = true

    if (this.useRequirementLevels) {
      const questions = await this.$axios.$get(
        `/${this.school}/general/${
          this.isProgram ? 'program' : 'course'
        }Template/questions`
      )
      if (questions) {
        const requirementLevels = _get(
          questions,
          'requisites.config.fields.requisitesSimple.config.fields.requirementLevels.config.options',
          []
        )
        this.requirementLevels = requirementLevels
      }
    }

    this.relatedRequisites = await this.fetchRequisiteData(this.value)
    this.isLoading = false
  },
  methods: {
    decamelize,
    getRequirementLevels(key) {
      return this.sortedValue.filter((v) => v.requirementLevel === key)
    },
    getRequirementLevelNotes(key) {
      const requirementLevel = this.documentRequirementLevels.find(
        (d) => d.key === key
      )
      if (requirementLevel) {
        return requirementLevel.notes || ''
      }
      return ''
    },
    isNoteNotEmpty(requisite) {
      return requisite.notes && requisite.notes !== '<p></p>'
    },
    isNotNarrativeText(type) {
      return type !== 'Narrative Text'
    },
    totalRequisitesCredits(requisites) {
      let requirementsCount = 0
      const entityName = this.isProgram ? 'Program' : 'Course'
      const { min, max } = requisites.reduce(
        (acc, requisite) => {
          requirementsCount++
          const { min, max } = this.getRequisitesCreditsRange(
            this.relatedRequisites,
            requisite.rules
          )

          acc.min += min
          acc.max += max

          return acc
        },
        {
          min: 0,
          max: 0,
        }
      )

      return `${requirementsCount} ${entityName} Requirements Totaling ${min} - ${max} Credits`
    },
  },
}
</script>

<style scoped>
.toggle-icon {
  color: #820000;
  border: 0.1rem solid #820000;
  width: 1.1rem;
  height: 1.1rem;
  padding: 2px;
  border-radius: 100%;
}
.toggle-icon-level {
  color: black;
  width: 1.2rem;
  height: 1.2rem;
  padding: 2px;
}
.small-info-icon {
  height: 0.85rem;
  width: 0.85rem;
}
</style>
